import { useMemo, useState } from 'react';
import { Button, Input, Message, Popover } from '@arco-design/web-react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store';
import { setHistoryMsg } from '@/store/slices/room';
import RtcClient from '@/lib/RtcClient';
import { useScene } from '@/lib/useCommon';
import { onUserUtteranceFinal } from '@/lib/turnRouter';
import { COMMAND, INTERRUPT_PRIORITY } from '@/utils/handler';
import style from './TopActions.module.less';

interface TopActionsProps {
  inPanel?: boolean;
}

function TopActions(props: TopActionsProps) {
  const { inPanel = false } = props;
  const dispatch = useDispatch();
  const room = useSelector((state: RootState) => state.room);
  const { roomId, answerMode, externalLlm, localUser } = room;
  const { botName } = useScene();
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const modelBadgeText = useMemo(() => {
    if (answerMode === 'doubao-llm') return null;
    if (answerMode === 'text-broadcast') return '文字播报';
    return externalLlm.model || externalLlm.provider || '外部LLM';
  }, [answerMode, externalLlm]);

  const copyRoomId = async () => {
    if (!roomId) return;

    try {
      await navigator.clipboard.writeText(roomId);
      Message.success('Room ID 已复制');
    } catch {
      Message.error('Room ID 复制失败');
    }
  };

  const sendText = async () => {
    const content = text.trim();
    if (!content) return;
    if (!botName) {
      Message.error('助手未就绪');
      return;
    }
    if (!RtcClient.getAgentEnabled()) {
      Message.error('AI 尚未启动');
      return;
    }

    setSending(true);
    try {
      dispatch(
        setHistoryMsg({
          text: content,
          user: localUser.userId || localUser.username || 'user',
          paragraph: true,
          definite: true,
        })
      );

      if (answerMode === 'doubao-llm') {
        RtcClient.commandAgent({
          agentName: botName,
          command: COMMAND.EXTERNAL_TEXT_TO_LLM,
          interruptMode: INTERRUPT_PRIORITY.HIGH,
          message: content,
        });
      } else {
        await onUserUtteranceFinal(content);
      }

      setText('');
      Message.success('文字已发送');
    } catch (e) {
      Message.error(`发送失败: ${(e as Error).message || String(e)}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`${style.topActions} ${inPanel ? style.inPanel : ''}`}>
      {roomId ? (
        <Popover
          trigger="click"
          position="bl"
          content={
            <div className={style.roomPopover}>
              <div className={style.popoverTitle}>房间 ID</div>
              <div className={style.roomIdText}>{roomId}</div>
              <Button size="mini" type="primary" onClick={copyRoomId}>
                复制
              </Button>
            </div>
          }
        >
          <button
            className={`${style.topActionBadge} ${inPanel ? style.inPanelBadge : ''}`}
            type="button"
          >
            房间
          </button>
        </Popover>
      ) : null}
      <Popover
        trigger="click"
        position="bl"
        content={
          <div className={style.textReplyPopover}>
            <div className={style.popoverTitle}>自定义助手模式</div>
            <div className={style.popoverHint}>输入文字后直接让助手回复，无需开麦说话。</div>
            <Input.TextArea
              rows={4}
              value={text}
              placeholder="输入要发给助手的文字"
              onChange={setText}
              onKeyDown={(event) => {
                if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') sendText();
              }}
            />
            <Button
              long
              type="primary"
              loading={sending}
              disabled={!text.trim()}
              onClick={sendText}
            >
              发送文字
            </Button>
          </div>
        }
      >
        <button
          className={`${style.topActionBadge} ${inPanel ? style.inPanelBadge : ''}`}
          type="button"
        >
          自定义助手
        </button>
      </Popover>
      {modelBadgeText ? (
        <Popover
          trigger="click"
          position="bl"
          content={
            <div style={{ minWidth: 160 }}>
              <div className={style.popoverTitle}>当前模型</div>
              <div style={{ marginTop: 6, color: '#1d2129', fontSize: 13 }}>
                {answerMode === 'text-broadcast'
                  ? '文字播报模式'
                  : `${externalLlm.provider} / ${externalLlm.model}`}
              </div>
              {answerMode === 'external-llm' && externalLlm.baseURL ? (
                <div
                  style={{ marginTop: 4, color: '#86909c', fontSize: 11, wordBreak: 'break-all' }}
                >
                  {externalLlm.baseURL}
                </div>
              ) : null}
            </div>
          }
        >
          <button
            className={`${style.topActionBadge} ${style.modelBadge} ${
              inPanel ? style.inPanelBadge : ''
            }`}
            type="button"
          >
            {modelBadgeText}
          </button>
        </Popover>
      ) : null}
    </div>
  );
}

export default TopActions;
